// src/pages/inventory/ordering/drawers/EditSalesOrderDrawer.tsx
import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { extractValidationErrors } from "../../../../../utils/apiError";
import { Alert, Button, Descriptions, Divider, Form, Input, InputNumber, DatePicker, Select, Spin, Typography, Modal, Row, Col, Space, Statistic } from "antd";
import type { ColumnsType } from "antd/es/table";
import { SaveOutlined, DeleteOutlined, PlusOutlined, MinusCircleOutlined, BarcodeOutlined, ShopOutlined, TagOutlined, ShoppingCartOutlined, InfoCircleOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import type { SalesOrder, SalesOrderItem, SalesOrderLabel } from "../../../../../types/inventory/OrderTypes";
import { createSalesOrder, updateSalesOrder, deleteSalesOrder, fetchSalesOrder } from "../../../../../api/inventory/salesOrderApi";
import { fetchProducts } from "../../../../../api/inventory/productApi";
import type { Product } from "../../../../../types/inventory/ProductTypes";
import { fetchProductLabels, assignStockLabel } from "../../../../../api/inventory/stockApi";
import type { StockLabelDetail } from "../../../../../types/inventory/StockTypes";
import { useNotifications } from "../../../../../context/NotificationContext";
import { Company } from "../../../../../types/admin/CompanyTypes";
import { fetchClinicCompany } from "../../../../../api/admin/companyApi";
import { DetailDrawer, SectionCard, DataTable, StatusTag, ContrastTag } from "../../../../../components/shared";
import CopyKitAssignmentLinkButton, { canCopyKitAssignmentLink } from "./CopyKitAssignmentLinkButton";
import { fetchPatientsByIds } from "../../../../../api/healthcare/PatientApi";
import { canViewPatients } from "../../../../../utils/userPrefs/validatePermissions";
import PatientSelect from "../../../../../components/finance/invoicing/PatientSelect";
import { patientLabel } from "../../../../../components/finance/invoicing/patientSearch";

const { Text } = Typography;

interface EditSalesOrderDrawerProps {
	isOpen: boolean;
	onClose: () => void;
	order: SalesOrder;
}

const EditSalesOrderDrawer: React.FC<EditSalesOrderDrawerProps> = ({ isOpen, onClose, order }) => {
	const [updatedOrder, setUpdatedOrder] = useState<SalesOrder>({ ...order });
	const [fullOrder, setFullOrder] = useState<SalesOrder | null>(null);
	const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
	const [errorMessages, setErrorMessages] = useState<string[]>([]);
	const [products, setProducts] = useState<Product[]>([]);
	const [items, setItems] = useState<SalesOrderItem[]>(order.items || []);
	const [activeTab, setActiveTab] = useState("details");
	const [availableLabelsByProduct, setAvailableLabelsByProduct] = useState<Record<number, StockLabelDetail[]>>({});
	const [assigningLabelKey, setAssigningLabelKey] = useState<string | null>(null);
	const { showError, showSuccess } = useNotifications();
	const [company, setCompany] = useState<Company | null>(null);

	const isShopifyOrder = order.is_shopify_order;
	const showKitLink = !!order.sales_order_id && canCopyKitAssignmentLink();
	const hasLabels = (fullOrder?.labels?.length || 0) > 0;
	const assigneeId = order.assignee_id ? String(order.assignee_id) : "";

	// Commerce stores only the uuid; the name comes from healthcare under the viewer's own permission.
	const { data: assignee, isLoading: assigneeLoading } = useQuery({
		queryKey: ["salesOrderAssignee", assigneeId],
		queryFn: async () => (await fetchPatientsByIds([assigneeId])).find((patient) => String(patient.id).toLowerCase() === assigneeId.toLowerCase()) ?? null,
		enabled: !!order.sales_order_id && !isShopifyOrder && !!assigneeId && canViewPatients(),
	});

	useEffect(() => {
		const loadCompany = async () => {
			let company: Company = await fetchClinicCompany();
			setCompany(company);
		};
		loadCompany();
	}, []);

	useEffect(() => {
		setUpdatedOrder({ ...order });
		setItems(order.items || []);
		if (order.sales_order_id) {
			loadFullOrder(order.sales_order_id);
		}
		if (company) {
			loadProducts();
		}
	}, [company, order]);

	useEffect(() => {
		calculateTotal();
	}, [items]);

	useEffect(() => {
		const productIds = Array.from(new Set(items.map((item) => item.product_id).filter(Boolean)));
		productIds.forEach((productId) => {
			if (!availableLabelsByProduct[productId]) {
				loadProductLabels(productId);
			}
		});
	}, [items]);

	const loadFullOrder = async (orderId: number) => {
		try {
			const fullOrderData = await fetchSalesOrder(orderId);
			setFullOrder(fullOrderData);
			setUpdatedOrder(fullOrderData);
			setItems(fullOrderData.items || []);
		} catch (error) {
			console.error("Error loading full order:", error);
		}
	};

	const loadProducts = async () => {
		try {
			const { data } = await fetchProducts(1, 100, company.id, "", undefined);
			setProducts(data);
		} catch (error) {
			console.error("Error loading products:", error);
		}
	};

	const loadProductLabels = async (productId: number) => {
		try {
			const { data } = await fetchProductLabels(productId, { status: "available" });
			setAvailableLabelsByProduct((prev) => ({ ...prev, [productId]: data }));
		} catch (error) {
			console.error("Error loading product labels:", error);
		}
	};

	// Builds the option list for a label picker: the currently-held label (if any, so it
	// still displays correctly even though it is no longer "available") plus the product's
	// available label pool.
	const buildLabelOptions = (productId: number, currentLabel?: SalesOrderLabel) => {
		const available = availableLabelsByProduct[productId] || [];
		const options = available.map((label) => ({ value: label.id, label: label.label_id }));
		if (currentLabel && !options.some((option) => option.value === currentLabel.stock_label_id)) {
			options.unshift({
				value: currentLabel.stock_label_id,
				label: currentLabel.stock_label?.label_id || `#${currentLabel.stock_label_id}`,
			});
		}
		return options;
	};

	const handleLabelAssign = async (item: SalesOrderItem, newStockLabelId: number, replaceStockLabelId?: number) => {
		if (!order.sales_order_id || !item.so_item_id) return;
		const key = `${item.so_item_id}-${replaceStockLabelId ?? "new"}`;
		setAssigningLabelKey(key);
		try {
			await assignStockLabel({
				label_id: newStockLabelId,
				sales_order_id: order.sales_order_id,
				sales_order_item_id: item.so_item_id,
				...(replaceStockLabelId ? { replace_stock_label_id: replaceStockLabelId } : {}),
			});
			showSuccess("Label assigned successfully");
			await loadFullOrder(order.sales_order_id);
			await loadProductLabels(item.product_id);
		} catch (error) {
			setErrorMessages(extractValidationErrors(error));
			showError("Failed to assign label");
		} finally {
			setAssigningLabelKey(null);
		}
	};

	const calculateTotal = () => {
		const subtotal = items.reduce((sum, item) => sum + ((Number(item.quantity) || 0) * (Number(item.unit_price) || 0)), 0);
		const tax = Number(updatedOrder.shopify_total_tax) || 0;
		const shipping = Number(updatedOrder.shopify_total_shipping) || 0;
		const total = subtotal + tax + shipping;
		setUpdatedOrder((prev) => ({ ...prev, total_amount: total }));
	};

	const addItem = () => {
		setItems([...items, { product_id: 0, variant_id: undefined, quantity: 1, unit_price: 0, notes: "" }]);
	};

	const removeItem = (index: number) => {
		setItems(items.filter((_, i) => i !== index));
	};

	const updateItem = (index: number, field: string, value: any) => {
		const newItems = [...items];
		newItems[index] = { ...newItems[index], [field]: value };

		if (field === "product_id") {
			const product = products.find((p) => p.product_id === value);
			if (product && product.unit_price) {
				newItems[index].unit_price = product.unit_price;
			}
			// Reset variant when product changes
			newItems[index].variant_id = undefined;
			newItems[index].variant = undefined;
			// Auto-select if only one variant
			if (product?.variants?.length === 1) {
				newItems[index].variant_id = product.variants[0].id;
				newItems[index].variant = product.variants[0];
			}
		}

		setItems(newItems);
	};

	const saveOrder = async () => {
		setIsSubmitting(true);
		setErrorMessages([]);
		try {
			if (!updatedOrder.assignee_id || items.length === 0) {
				setErrorMessages(["Patient and at least one item are required."]);
				setIsSubmitting(false);
				return;
			}

			const orderData = { ...updatedOrder, items };

			if (order.sales_order_id) {
				const response = await updateSalesOrder(order.sales_order_id, orderData);
				setUpdatedOrder(response);
				showSuccess("Sales order updated successfully");
			} else {
				const newOrder = await createSalesOrder(orderData);
				setUpdatedOrder(newOrder);
				showSuccess("Sales order created successfully");
			}
			onClose();
		} catch (error: unknown) {
			handleErrors(error);
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleDelete = () => {
		Modal.confirm({
			title: "Delete Sales Order",
			content: "Are you sure you want to delete this sales order? This action cannot be undone.",
			okText: "Delete",
			okType: "danger",
			cancelText: "Cancel",
			onOk: async () => {
				try {
					if (order.sales_order_id) {
						await deleteSalesOrder(order.sales_order_id);
						showSuccess("Sales order deleted successfully");
						onClose();
					}
				} catch (error) {
					handleErrors(error);
				}
			},
		});
	};

	const handleErrors = (error: unknown) => {
		setErrorMessages(extractValidationErrors(error));
		showError("Failed to save sales order");
	};

	// Helper function to get orphaned labels (not assigned to any specific item)
	const getOrphanedLabels = () => {
		if (!fullOrder?.labels) return [];
		return fullOrder.labels.filter(label => !label.sales_order_item_id);
	};

	const itemColumns: ColumnsType<SalesOrderItem & { index: number }> = [
		{
			title: "Product",
			dataIndex: "product_id",
			key: "product_id",
			render: (value: number, record, index: number) =>
				isShopifyOrder || record.so_item_id ? (
					<Text>{record.product?.name}</Text>
				) : (
					<Select
						value={value || undefined}
						placeholder="Select product"
						style={{ width: "100%" }}
						showSearch
						filterOption={(input, option) => (option?.label as string)?.toLowerCase().includes(input.toLowerCase())}
						options={products.map((p) => ({ value: p.product_id, label: p.name }))}
						onChange={(val) => updateItem(index, "product_id", val)}
					/>
				),
		},
		{
			title: "Variant",
			dataIndex: "variant_id",
			key: "variant_id",
			width: 150,
			render: (value: number | undefined, record, index: number) => {
				const product = record.product_id ? products.find((p) => p.product_id === record.product_id) : null;
				const variants = product?.variants ?? [];
				if (isShopifyOrder || record.so_item_id) {
					return <Text type="secondary">{record.variant?.formatted_title ?? record.variant?.title ?? (variants.length === 1 ? "Default" : "—")}</Text>;
				}
				if (variants.length <= 1) {
					return <Text type="secondary">Default</Text>;
				}
				return (
					<Select
						value={value || undefined}
						placeholder="Select variant"
						style={{ width: "100%" }}
						options={variants.map((v) => ({ value: v.id, label: v.formatted_title ?? v.title ?? v.sku }))}
						onChange={(val) => updateItem(index, "variant_id", val)}
					/>
				);
			},
		},
		{
			title: "Quantity",
			dataIndex: "quantity",
			key: "quantity",
			width: 120,
			render: (value: number, record, index: number) => (isShopifyOrder ? <Text>{value}</Text> : <InputNumber min={1} value={value} onChange={(val) => updateItem(index, "quantity", val || 1)} style={{ width: "100%" }} />),
		},
		{
			title: "Unit Price",
			dataIndex: "unit_price",
			key: "unit_price",
			width: 130,
			render: (value: number, record, index: number) =>
				isShopifyOrder ? <Text>${Number(value || 0).toFixed(2)}</Text> : <InputNumber prefix="$" precision={2} min={0} value={value} onChange={(val) => updateItem(index, "unit_price", val || 0)} style={{ width: "100%" }} />,
		},
		{
			title: "Total",
			key: "total",
			width: 100,
			align: "right",
			render: (_, record) => (
				<Text strong style={{ fontSize: 14 }}>
					${((Number(record.quantity) || 0) * (Number(record.unit_price) || 0)).toFixed(2)}
				</Text>
			),
		},
		{
			title: "Labels",
			key: "labels",
			width: 220,
			// Editable regardless of isShopifyOrder — label assignment is a staff correction
			// on top of FIFO auto-assignment, not part of the order's Shopify-sourced data.
			render: (_, record) => {
				if (!order.sales_order_id || !record.so_item_id) {
					return <Text type="secondary">{record.sales_order_labels?.length ? `${record.sales_order_labels.length} assigned` : "No labels assigned"}</Text>;
				}

				const existingLabels = record.sales_order_labels || [];
				const filterLabelOption = (input: string, option?: { label: string }) => (option?.label as string)?.toLowerCase().includes(input.toLowerCase());

				if (existingLabels.length === 0) {
					return (
						<Select
							size="small"
							style={{ width: "100%" }}
							placeholder="Assign label"
							showSearch
							loading={assigningLabelKey === `${record.so_item_id}-new`}
							options={buildLabelOptions(record.product_id)}
							filterOption={filterLabelOption}
							onChange={(val) => handleLabelAssign(record, val)}
						/>
					);
				}

				return (
					<Space direction="vertical" size={4} style={{ width: "100%" }}>
						{existingLabels.map((label) => (
							<Select
								key={label.id}
								size="small"
								style={{ width: "100%" }}
								value={label.stock_label_id}
								showSearch
								loading={assigningLabelKey === `${record.so_item_id}-${label.stock_label_id}`}
								options={buildLabelOptions(record.product_id, label)}
								filterOption={filterLabelOption}
								onChange={(val) => handleLabelAssign(record, val, label.stock_label_id)}
								suffixIcon={<BarcodeOutlined />}
							/>
						))}
					</Space>
				);
			},
		},
		...(isShopifyOrder
			? []
			: [
					{
						title: "",
						key: "actions",
						width: 50,
						render: (_: any, record: any, index: number) => <Button type="text" danger size="small" icon={<MinusCircleOutlined />} onClick={() => removeItem(index)} />,
					},
				]),
	];



	const subtotal = items.reduce((sum, item) => sum + ((Number(item.quantity) || 0) * (Number(item.unit_price) || 0)), 0);
	const tax = Number(updatedOrder.shopify_total_tax) || 0;
	const shipping = Number(updatedOrder.shopify_total_shipping) || 0;
	const total = subtotal + tax + shipping;

	const DetailsTab = (
		<Space direction="vertical" style={{ width: "100%" }} size={16}>
			{isShopifyOrder && (
				<Alert
					message={<Space><ShopOutlined /> Shopify Order</Space>}
					description="This order was imported from Shopify. Most fields are read-only to maintain data consistency, though item labels can still be reassigned from the Items & Labels tab."
					type="info"
					showIcon
				/>
			)}

			<SectionCard title="Order Summary">
				<Row gutter={[16, 0]}>
					<Col xs={24} md={6}>
						<Statistic
							title="Order ID"
							value={isShopifyOrder ? `#${updatedOrder.shopify_order_number}` : `SO-${String(updatedOrder.sales_order_id || "NEW").padStart(4, "0")}`}
							prefix={isShopifyOrder ? <ShopOutlined /> : <ShoppingCartOutlined />}
						/>
					</Col>
					<Col xs={24} md={6}>
						<div>
							<Text type="secondary" style={{ fontSize: 13 }}>Status</Text>
							<div style={{ marginTop: 4 }}>
								<StatusTag status={updatedOrder.status || ""} />
							</div>
						</div>
					</Col>
					<Col xs={24} md={6}>
						<Statistic title="Total Amount" value={total} precision={2} prefix="$" valueStyle={{ color: "var(--mg-success)", fontWeight: 600 }} />
					</Col>
					<Col xs={24} md={6}>
						<Statistic title="Items" value={items.length} suffix="items" />
					</Col>
				</Row>
			</SectionCard>

			<SectionCard title={isShopifyOrder ? "Customer Information" : "Patient Information"}>
				{isShopifyOrder ? (
					<Descriptions column={2} bordered>
						<Descriptions.Item label="Customer Name">{updatedOrder.shopify_customer_name || "—"}</Descriptions.Item>
						<Descriptions.Item label="Email">{updatedOrder.shopify_customer_email || "—"}</Descriptions.Item>
						<Descriptions.Item label="Shopify Customer ID">{updatedOrder.shopify_customer_id || "—"}</Descriptions.Item>
						<Descriptions.Item label="Financial Status">
							<ContrastTag background="var(--mg-info)">{updatedOrder.shopify_financial_status?.toUpperCase() || "—"}</ContrastTag>
						</Descriptions.Item>
						<Descriptions.Item label="Fulfillment Status" span={2}>
							<ContrastTag background="var(--mg-primary)">{updatedOrder.shopify_fulfillment_status?.toUpperCase() || "—"}</ContrastTag>
						</Descriptions.Item>
					</Descriptions>
				) : order.sales_order_id ? (
					// Read-only: commerce's update() ignores the assignee, so an edit here would be silently dropped.
					<Descriptions column={1} bordered>
						<Descriptions.Item label="Patient">
							{!assigneeId ? (
								"—"
							) : assigneeLoading ? (
								<Spin size="small" />
							) : assignee ? (
								<Space direction="vertical" size={0}>
									<Text strong>{patientLabel(assignee)}</Text>
									{assignee.email && <Text type="secondary">{assignee.email}</Text>}
								</Space>
							) : (
								<Space direction="vertical" size={0}>
									<Text>Unknown patient</Text>
									<Text type="secondary">{assigneeId}</Text>
								</Space>
							)}
						</Descriptions.Item>
					</Descriptions>
				) : (
					<Form layout="vertical">
						<Form.Item label="Patient" required>
							<PatientSelect
								value={updatedOrder.assignee_id ? String(updatedOrder.assignee_id) : undefined}
								onChange={(value) => setUpdatedOrder((prev) => ({ ...prev, assignee_type: "user", assignee_id: value ?? "" }))}
								data-testid="sales-order-patient-select"
							/>
						</Form.Item>
					</Form>
				)}
			</SectionCard>

			<SectionCard title="Order Details">
				<Form layout="vertical">
					<Row gutter={[16, 0]}>
						<Col xs={24} md={12}>
							<Form.Item label="Order Date">
								<DatePicker
									style={{ width: "100%" }}
									value={updatedOrder.order_date ? dayjs(updatedOrder.order_date) : null}
									onChange={(date) =>
										setUpdatedOrder((prev) => ({
											...prev,
											order_date: date ? date.toISOString() : "",
										}))
									}
									disabled={isShopifyOrder}
								/>
							</Form.Item>
						</Col>
						<Col xs={24} md={12}>
							<Form.Item label="Status">
								<Select value={updatedOrder.status} onChange={(value) => setUpdatedOrder((prev) => ({ ...prev, status: value }))} disabled={isShopifyOrder}>
									<Select.Option value="pending">Pending</Select.Option>
									<Select.Option value="processing">Processing</Select.Option>
									<Select.Option value="completed">Completed</Select.Option>
									<Select.Option value="cancelled">Cancelled</Select.Option>
								</Select>
							</Form.Item>
						</Col>
						<Col xs={24}>
							<Form.Item label="Notes">
								<Input.TextArea rows={3} value={updatedOrder.notes || ""} onChange={(e) => setUpdatedOrder((prev) => ({ ...prev, notes: e.target.value }))} placeholder="Order notes..." disabled={isShopifyOrder} />
							</Form.Item>
						</Col>
					</Row>
				</Form>
			</SectionCard>

			{isShopifyOrder && (updatedOrder.shopify_shipping_address || updatedOrder.shopify_billing_address) && (
				<Row gutter={16}>
					{updatedOrder.shopify_shipping_address && (
						<Col xs={24} md={12}>
							<SectionCard title="Shipping Address">
								<Space direction="vertical" style={{ width: "100%" }}>
									{updatedOrder.shopify_shipping_address.name && <Text strong>{updatedOrder.shopify_shipping_address.name}</Text>}
									{updatedOrder.shopify_shipping_address.company && <Text>{updatedOrder.shopify_shipping_address.company}</Text>}
									{updatedOrder.shopify_shipping_address.address1 && <Text>{updatedOrder.shopify_shipping_address.address1}</Text>}
									{updatedOrder.shopify_shipping_address.address2 && <Text>{updatedOrder.shopify_shipping_address.address2}</Text>}
									{(updatedOrder.shopify_shipping_address.city || updatedOrder.shopify_shipping_address.province || updatedOrder.shopify_shipping_address.zip) && (
										<Text>
											{updatedOrder.shopify_shipping_address.city && `${updatedOrder.shopify_shipping_address.city}, `}
											{updatedOrder.shopify_shipping_address.province && `${updatedOrder.shopify_shipping_address.province} `}
											{updatedOrder.shopify_shipping_address.zip}
										</Text>
									)}
									{updatedOrder.shopify_shipping_address.country && <Text>{updatedOrder.shopify_shipping_address.country}</Text>}
									{updatedOrder.shopify_shipping_address.phone && <Text type="secondary">Phone: {updatedOrder.shopify_shipping_address.phone}</Text>}
								</Space>
							</SectionCard>
						</Col>
					)}
					{updatedOrder.shopify_billing_address && (
						<Col xs={24} md={12}>
							<SectionCard title="Billing Address">
								<Space direction="vertical" style={{ width: "100%" }}>
									{updatedOrder.shopify_billing_address.name && <Text strong>{updatedOrder.shopify_billing_address.name}</Text>}
									{updatedOrder.shopify_billing_address.company && <Text>{updatedOrder.shopify_billing_address.company}</Text>}
									{updatedOrder.shopify_billing_address.address1 && <Text>{updatedOrder.shopify_billing_address.address1}</Text>}
									{updatedOrder.shopify_billing_address.address2 && <Text>{updatedOrder.shopify_billing_address.address2}</Text>}
									{(updatedOrder.shopify_billing_address.city || updatedOrder.shopify_billing_address.province || updatedOrder.shopify_billing_address.zip) && (
										<Text>
											{updatedOrder.shopify_billing_address.city && `${updatedOrder.shopify_billing_address.city}, `}
											{updatedOrder.shopify_billing_address.province && `${updatedOrder.shopify_billing_address.province} `}
											{updatedOrder.shopify_billing_address.zip}
										</Text>
									)}
									{updatedOrder.shopify_billing_address.country && <Text>{updatedOrder.shopify_billing_address.country}</Text>}
									{updatedOrder.shopify_billing_address.phone && <Text type="secondary">Phone: {updatedOrder.shopify_billing_address.phone}</Text>}
								</Space>
							</SectionCard>
						</Col>
					)}
				</Row>
			)}
		</Space>
	);

	const ItemsTab = (
		<Space direction="vertical" style={{ width: "100%" }} size={16}>
			<SectionCard
				title={
					<Space>
						<ShoppingCartOutlined />
						Order Items & Labels
						<ContrastTag background="var(--mg-primary)">{items.length} items</ContrastTag>
						{hasLabels && (
							<ContrastTag background="var(--mg-success)" icon={<BarcodeOutlined />}>
								{fullOrder?.labels?.length ?? 0} labels
							</ContrastTag>
						)}
					</Space>
				}
				noPadding
				extra={
					!isShopifyOrder ? (
						<Button type="dashed" icon={<PlusOutlined />} onClick={addItem}>
							Add Item
						</Button>
					) : undefined
				}
			>
				<DataTable
					bare
					columns={itemColumns}
					dataSource={items.map((item, index) => ({ ...item, index }))}
					rowKey="index"
					pagination={false}
					emptyTitle="No items"
					emptyDescription="Add products to this order"
				/>
				{getOrphanedLabels().length > 0 && (
					<div style={{ padding: "12px 20px", borderTop: `1px solid var(--mg-border)` }}>
						<Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 8 }}>Unassigned Labels</Text>
						<Space wrap>
							{getOrphanedLabels().map((label) => (
								<ContrastTag key={label.id} icon={<BarcodeOutlined />} background="var(--mg-warning)">
									{label.stock_label?.label_id || `#${label.stock_label_id}`}
									<Text type="secondary" style={{ fontSize: 11, marginLeft: 4 }}>
										({new Date(label.assigned_at).toLocaleDateString()})
									</Text>
								</ContrastTag>
							))}
						</Space>
						<Text type="secondary" style={{ fontSize: 11, marginTop: 8, display: "block" }}>
							These labels are assigned to the order but not linked to specific items.
						</Text>
					</div>
				)}
			</SectionCard>

			<SectionCard title="Order Totals">
				<Space direction="vertical" style={{ width: "100%" }} size="middle">
					<Row justify="space-between">
						<Col><Text>Subtotal:</Text></Col>
						<Col><Text strong style={{ fontSize: 16 }}>${subtotal.toFixed(2)}</Text></Col>
					</Row>
					{isShopifyOrder && tax > 0 && (
						<Row justify="space-between">
							<Col><Text>Tax:</Text></Col>
							<Col><Text strong>${tax.toFixed(2)}</Text></Col>
						</Row>
					)}
					{isShopifyOrder && shipping > 0 && (
						<Row justify="space-between">
							<Col><Text>Shipping:</Text></Col>
							<Col><Text strong>${shipping.toFixed(2)}</Text></Col>
						</Row>
					)}
					<Divider style={{ margin: "8px 0" }} />
					<Row justify="space-between">
						<Col><Text strong style={{ fontSize: 18 }}>Total:</Text></Col>
						<Col><Text strong style={{ fontSize: 20, color: "var(--mg-success)" }}>${total.toFixed(2)}</Text></Col>
					</Row>
				</Space>
			</SectionCard>

			{!isShopifyOrder && <Alert message="Add products to this order by selecting them from the dropdown menu. Prices will be automatically filled based on product pricing." type="info" showIcon icon={<InfoCircleOutlined />} />}
		</Space>
	);



	const tabItems = [
		{
			key: "details",
			label: <Space><InfoCircleOutlined />Order Details</Space>,
			children: DetailsTab,
		},
		{
			key: "items",
			label: <Space><ShoppingCartOutlined />Items & Labels</Space>,
			children: ItemsTab,
		},
	];

	const titleNode = (
		<Space>
			{isShopifyOrder ? `Shopify Order #${updatedOrder.shopify_order_number}` : order.sales_order_id ? `Sales Order SO-${String(order.sales_order_id).padStart(4, "0")}` : "Create New Sales Order"}
			{isShopifyOrder && <ContrastTag icon={<ShopOutlined />} background="var(--mg-success)">Shopify</ContrastTag>}
			{hasLabels && <ContrastTag icon={<TagOutlined />} background="var(--mg-primary)">{fullOrder?.labels?.length} Label{(fullOrder?.labels?.length || 0) !== 1 ? "s" : ""}</ContrastTag>}
			<StatusTag status={updatedOrder.status || ""} />
		</Space>
	);

	return (
		<DetailDrawer
			open={isOpen}
			onClose={onClose}
			width="85vw"
			title={titleNode}
			headerActions={
				!isShopifyOrder || showKitLink ? (
					<>
						{showKitLink && <CopyKitAssignmentLinkButton salesOrderId={order.sales_order_id} />}
						{!isShopifyOrder && (
							<>
								<Button type="primary" icon={<SaveOutlined />} onClick={saveOrder} loading={isSubmitting}>
									Save
								</Button>
								{order.sales_order_id && (
									<Button danger icon={<DeleteOutlined />} onClick={handleDelete} disabled={isSubmitting}>
										Delete
									</Button>
								)}
							</>
						)}
					</>
				) : undefined
			}
			tabs={tabItems}
			activeTab={activeTab}
			onTabChange={setActiveTab}
			errorMessages={errorMessages}
			onClearErrors={() => setErrorMessages([])}
		/>
	);
};

export default EditSalesOrderDrawer;
